import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const TrainingModeContext = createContext({
  enabled: false,
  loading: true,
  refresh: () => {},
  setEnabled: () => {},
});

export const TrainingModeProvider = ({ children }) => {
  const { user } = useAuth();
  const [enabled, setEnabledState] = useState(false);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!user) {
      setEnabledState(false);
      setLoading(false);
      return;
    }
    try {
      const r = await api.get("/system/training-mode");
      setEnabledState(!!r.data?.enabled);
    } catch {
      // ignore – default OFF on error
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Poll every 30 seconds so an admin toggle is reflected for other users
  useEffect(() => {
    if (!user) return undefined;
    const id = setInterval(refresh, 30_000);
    return () => clearInterval(id);
  }, [user, refresh]);

  const setEnabled = useCallback(async (next) => {
    const r = await api.post("/admin/training-mode", { enabled: !!next });
    setEnabledState(!!r.data?.enabled);
    return r.data;
  }, []);

  return (
    <TrainingModeContext.Provider value={{ enabled, loading, refresh, setEnabled }}>
      {children}
    </TrainingModeContext.Provider>
  );
};

export const useTrainingMode = () => useContext(TrainingModeContext);
